import React, { useState, useEffect } from 'react';
import { includes } from 'lodash';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';

import RepeatableRecordingQuestion from 'components/Practice/RepeatablePracticeQuestion/shared/RepeatableRecordingQuestion';
import QuestionPrompt from 'components/Practice/Learner/QuestionPrompt';
import { RecordingTypes } from 'constants/recordingTypes';
import RepeatablePracticeTabs from './RepeatablePracticeTabs';
import MyResponsesTab from './MyResponsesTab';

const TAB_RECORD = 'record';
const TAB_RESPONSES = 'responses';

function RepeatablePracticeQuestion(props) {
  const { data, meta } = props;
  const { criteria } = data;
  const {
    features,
    exampleRecordingHtml,
    apiKey,
    playbackUrl,
    isVimeoProvider,
    exampleRecordingId,
  } = meta;
  const [userResponses, setUserResponses] = useState([]);
  const [selectedTab, setSelectedTab] = useState(
    data.recording ? TAB_RESPONSES : TAB_RECORD,
  );
  const [isTabDisabled, setIsTabDisabled] = useState(false);
  const [currentChoice, setCurrentChoice] = useState(null);

  /************* RECORDING STATES AND CONSTS ******************/

  const [finishedRecording, setFinishedRecording] = useState(!!data.recording);
  const [skipped, setSkipped] = useState(meta.previewing ? null : meta.skipped);
  const [technicalDifficultiesReported, setTechnicalDifficultiesReported] =
    useState(false);
  const [givenAnswer, setGivenAnswer] = useState('');
  const isRecordingQuestion = includes(RecordingTypes, data.questionType);
  const [sendFeedbackError, setSendFeedbackError] = useState(false);
  const [finished, setFinished] = useState(meta.finished);

  /************* SHARED ******************/

  useEffect(() => {
    if (!meta.previewing) {
      request
        .get(routes.LESSON_ATTEMPTS(data.lessonId), {
          question_id: data.questionId,
        })
        .then(response => {
          setCurrentChoice(
            response.find(
              userResponse =>
                userResponse.isAnswer === true &&
                userResponse.progressId === data.progressId,
            ),
          );
          setUserResponses(response);

          if (response.length === 0) {
            setIsTabDisabled(true);
            setSelectedTab(TAB_RECORD);
          }
        })
        .catch(e => {
          console.error(e);
          // TODO: Handle Server Error
        });
    } else if (meta.previewing) {
      setIsTabDisabled(true);
      setSelectedTab(TAB_RECORD);
    }
  }, [
    data.questionId,
    data.progressId,
    finishedRecording,
    data.lessonId,
    meta.previewing,
    selectedTab,
  ]);

  const resetAnswer = () => {
    request
      .post(props.meta.saveUrl, {
        answer: null,
        question_id: props.data.questionId,
      })
      .then(() => {
        setGivenAnswer(null);
        setSkipped(false);
        setSendFeedbackError(false);
        setTechnicalDifficultiesReported(false);
        setSelectedTab(TAB_RECORD);
      })
      .catch(e => {
        if (e.status === 200) {
          setGivenAnswer(null);
          return;
        }
        if (e.responseJSON.error === 'lesson_completed') {
          setFinished(true);
        }
        console.error(e);
        // TODO: Handle Server Error
      });
  };

  let contentComponent;
  switch (true) {
    case isRecordingQuestion: {
      contentComponent = (
        <RepeatableRecordingQuestion
          setFinishedRecording={setFinishedRecording}
          setIsTabDisabled={setIsTabDisabled}
          setCurrentChoice={setCurrentChoice}
          setSelectedTab={setSelectedTab}
          setSkipped={setSkipped}
          skipped={skipped}
          technicalDifficultiesReported={technicalDifficultiesReported}
          setTechnicalDifficultiesReported={setTechnicalDifficultiesReported}
          userResponses={userResponses}
          givenAnswer={givenAnswer}
          setGivenAnswer={setGivenAnswer}
          sendFeedbackError={sendFeedbackError}
          setSendFeedbackError={setSendFeedbackError}
          finished={finished}
          resetAnswer={resetAnswer}
          {...props}
        />
      );
      break;
    }
    default:
      contentComponent = null;
  }

  return (
    <div className="PracticeQuestion">
      {(data.criteria.length > 0 ||
        exampleRecordingHtml ||
        isVimeoProvider) && (
        <QuestionPrompt
          criteria={criteria}
          features={features}
          exampleRecordingHtml={exampleRecordingHtml}
          apiKey={apiKey}
          url={playbackUrl}
          isVimeoProvider={isVimeoProvider}
          exampleRecordingId={exampleRecordingId}
          questionType={data.questionType}
        />
      )}
      <RepeatablePracticeTabs
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        userResponses={userResponses}
        isTabDisabled={isTabDisabled}
        isRecordingQuestion={isRecordingQuestion}
        previewing={meta.previewing}
      />

      {selectedTab === TAB_RECORD ? (
        contentComponent
      ) : (
        <MyResponsesTab
          userResponses={userResponses}
          isRecordingQuestion={isRecordingQuestion}
          setUserResponses={setUserResponses}
          currentChoice={currentChoice}
          resetAnswer={resetAnswer}
          setCurrentChoice={setCurrentChoice}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          setIsTabDisabled={setIsTabDisabled}
          skipped={skipped}
          setSkipped={setSkipped}
          technicalDifficultiesReported={technicalDifficultiesReported}
          {...props}
        />
      )}
    </div>
  );
}

export default props => <RepeatablePracticeQuestion {...props} />;
